import { FC, memo, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Table from "../../components/new/Table/Table";
import Filters from "./Filters/Filters";
import Loader from "../../components/new/Loader/Loader";
import { ChainsFilters, iChainsFilters } from "../../core/models/ChainsFilters";
import { iChainBase } from "../../core/models/ChainBase";
import { useTableConfig } from "./useTableConfig";
import { iTopChainsData } from "../../core/models/TopChainsData";
import useTableSort from "../../components/new/Table/useTableSort";
import useChainsMenuFilter from "@apiHooks/useChainsMenuFilter";
import './NewTopChains.styles.scss';
import { CopyButtonContext } from "../../components/new/Table/CopyButton";
import { equalInLC, hasMatches, normalizeMetro } from "@helpers";
import ChainsFoodsTab from "./ChainsFoodsTab/ChainsFoodsTab";
import { PlacesFoodsTab } from "./PlacesFoodsTabs/PlacesFoodsTabs.helpers";
import useChainsMenuItems from "@apiHooks/useChainsMenuItems";
import { getChainsClipboardData } from "./NewTopChains.helpers";

const EXPORT_MAX_LENGTH = 50;

function getDefaultColumnSorting(fieldName: keyof iChainBase | null): 'asc' | 'desc' {
    if (!fieldName) return 'asc';
    const descColumns: Array<keyof iChainBase> = ["numberOfUnits", "unitsGrowth", "sales"];
    if (descColumns.includes(fieldName)) return 'desc';
    return 'asc';
}

interface iProps {
    data: iTopChainsData;
    setCount: (count: number) => void;
    hasFilters?: boolean;
    handleFiltersChange: (selectedFilters: iChainsFilters, chainsIds: number[]) => void;
    filtersTestId?: string;
    tableTestId?: string;
    initialFilters?: Partial<iChainsFilters>;
    initialLocationsTab?: LocationsTab;
    activeTab?: PlacesFoodsTab;
}

export type LocationsTab = "metros" | "states";

const NewTopChains: FC<iProps> = (
    {
        data,
        setCount,
        hasFilters = false,
        handleFiltersChange,
        filtersTestId,
        tableTestId,
        initialFilters,
        initialLocationsTab,
        activeTab = "places",
    }
) => {
    const { places: rawPlaces, filtersData } = data;
    const [selectedFilters, setSelectedFilters] = useState<iChainsFilters>(
        () => new ChainsFilters({ subModel: initialFilters })
    );
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [selectedLocationsTab, setSelectedLocationsTab] = useState<LocationsTab>(
        initialLocationsTab ?? "states"
    );

    const { includes, excludes } = selectedFilters;
    const isMenuFilterActive = includes.length > 0 || excludes.length > 0;
    const isMenuCountColumnVisible = includes.length > 0;
    const {
        data: menuChains,
        isLoading: isMenuLoading,
        isSuccess: isMenuSuccess,
    } = useChainsMenuFilter(includes, excludes);

    const menuChainIdSet = useMemo(() => new Set(menuChains.map(i => i.chainId)), [menuChains]);
    const menuItemCountById = useMemo(
        () => new Map(menuChains.map(i => [i.chainId, i.menuItemCount])),
        [menuChains]
    );
    const isMenuFilterLoading = isMenuFilterActive && isMenuLoading;

    const filteredPlaces = useMemo(
        () => {
            const {
                segments: selectedSegments,
                menuTypes: selectedMenuTypes,
                metros: selectedMetros,
                states: selectedStates,
                minUnits: selectedMinUnits,
                maxUnits: selectedMaxUnits,
                alcohol: selectedAlcohol
            } = selectedFilters;
            const {
                segments: allSegments,
                menuTypes: allMenuTypes,
                metros: allMetros,
                states: allStates,
                alcohol: allAlcohol
            } = filtersData;

            // Match territory-sourced metro/state options against the chains' own
            // values via normalization. Sets built once per recompute.
            const isStatesFilterActive = selectedStates.length > 0
                && selectedStates.length !== allStates.length && selectedLocationsTab === "states";
            const isMetrosFilterActive = selectedMetros.length > 0
                && selectedMetros.length !== allMetros.length && selectedLocationsTab === "metros";
            const selectedStatesNorm = isStatesFilterActive
                ? new Set(selectedStates.map(s => s.toLowerCase()))
                : null;
            const selectedMetrosNorm = isMetrosFilterActive
                ? new Set(selectedMetros.map(normalizeMetro))
                : null;

            return rawPlaces.filter(place => {
                let isMatched = true;
                if (selectedSegments.length && selectedSegments.length !== allSegments.length)
                    isMatched = isMatched && selectedSegments.includes(place.segment);
                if (selectedStatesNorm)
                    isMatched = isMatched && selectedStatesNorm.has((place.stateFullName ?? '').toLowerCase());
                if (selectedMetrosNorm)
                    isMatched = isMatched && selectedMetrosNorm.has(normalizeMetro(place.metroArea ?? ''));
                if (selectedMenuTypes.length && selectedMenuTypes.length !== allMenuTypes.length)
                    isMatched = isMatched && selectedMenuTypes.includes(place.menuType);
                if (selectedAlcohol.length && selectedAlcohol.length !== allAlcohol.length)
                    isMatched = isMatched && selectedAlcohol.includes(place.alcohol);
                if (isMenuFilterActive)
                    isMatched = isMatched && menuChainIdSet.has(place.id);
                if (!!searchQuery.trim()) {
                    isMatched = isMatched && hasMatches(place.name, searchQuery);
                }
                isMatched = isMatched && place.numberOfUnits >= selectedMinUnits;
                isMatched = isMatched && place.numberOfUnits <= selectedMaxUnits;
                return isMatched;
            });
        },
        [searchQuery, rawPlaces, selectedFilters, filtersData, selectedLocationsTab, isMenuFilterActive, menuChainIdSet]
    );

    const placesWithMenuCount = useMemo(
        () => isMenuCountColumnVisible
            ? filteredPlaces.map(place => ({
                ...place,
                menuItemCount: menuItemCountById.get(place.id) ?? null,
            }))
            : filteredPlaces,
        [filteredPlaces, isMenuCountColumnVisible, menuItemCountById]
    );

    const {
        handleSort,
        sortingField,
        sortingDirection,
        getColumnIcon,
        setSortingDirection,
        sortedData: places
    } = useTableSort(placesWithMenuCount, "numberOfUnits");

    const tableConfig = useTableConfig(
        places,
        sortingField,
        sortingDirection,
        handleSort,
        getColumnIcon,
        searchQuery,
        isMenuCountColumnVisible,
        includes,
        excludes,
    );

    const placeIds = useMemo(() => places.map(i => i.id), [places]);

    const {
        data: menuItemRows,
        isLoading: isMenuItemsLoading,
        isSuccess: isMenuItemsSuccess,
    } = useChainsMenuItems(includes, excludes);

    const isFoodsTabActive = activeTab === "foods";

    useEffect(() => {
        if (isMenuFilterLoading) return;
        handleFiltersChange(
            {
                ...selectedFilters,
                states: selectedLocationsTab === "states" ? selectedFilters.states : [],
                metros: selectedLocationsTab === "metros" ? selectedFilters.metros : [],
            },
            placeIds
        );
    }, [selectedFilters, selectedLocationsTab, placeIds, isMenuFilterLoading]);

    useEffect(() => {
        if (isMenuFilterActive && isMenuSuccess === false) {
            toast.error("Couldn't apply the On the Menu filter. Please try again.");
        }
    }, [isMenuFilterActive, isMenuSuccess]);

    useEffect(() => {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        setSelectedFilters((prev) => {
            const intersect = (
                selected: string[],
                available: string[],
                isEqual: (a: string, b: string) => boolean = equalInLC,
            ): string[] =>
                available.length === 0
                    ? selected
                    : selected
                        .map(v => available.find(a => isEqual(a, v)))
                        .filter((a): a is string => a !== undefined);

            const segments = intersect(prev.segments, filtersData.segments);
            const states = intersect(prev.states, filtersData.states);
            const metros = intersect(
                prev.metros,
                filtersData.metros,
                (a, b) => normalizeMetro(a) === normalizeMetro(b),
            );

            return new ChainsFilters({
                subModel: {
                    ...prev,
                    maxUnits: filtersData.maxUnits,
                    segments,
                    states,
                    metros,
                },
            });
        });
    }, [filtersData]);

    useEffect(() => {
        setSortingDirection(getDefaultColumnSorting(sortingField));
    }, [sortingField, setSortingDirection]);

    useEffect(() => {
        if (isMenuFilterLoading) return;
        setCount(places.length);
    }, [places, isMenuFilterLoading]);


    const getClipboardData = useMemo(
        () => ({ head, data }: { head: string[], data: iChainBase[] }) =>
            getChainsClipboardData(head, data, isMenuCountColumnVisible),
        [isMenuCountColumnVisible]
    );


    return (
        <>
            {hasFilters && (
                <div
                    className="NewTopChains__filters"
                >
                    <Filters
                        searchQuery={searchQuery}
                        setSearchQuery={setSearchQuery}
                        data={filtersData}
                        selectedFilters={selectedFilters}
                        applyFilters={setSelectedFilters}
                        selectedLocationsTab={selectedLocationsTab}
                        setSelectedLocationsTab={setSelectedLocationsTab}
                        testId={filtersTestId}
                    />
                </div>
            )}
            {isFoodsTabActive && !isMenuFilterLoading && (
                <ChainsFoodsTab
                    includes={includes}
                    chainIds={placeIds}
                    rows={menuItemRows}
                    isLoading={isMenuItemsLoading}
                    isSuccess={isMenuItemsSuccess}
                />
            )}
            {(!isFoodsTabActive || isMenuFilterLoading) && (
                <div className="NewTopChains__table">
                    {isMenuFilterLoading ? (
                        <Loader/>
                    ) : (
                        <CopyButtonContext.Provider value={{
                            data: getClipboardData({
                                head: tableConfig.map(item => item.title || ""),
                                data: places.slice(0, EXPORT_MAX_LENGTH)
                            }),
                        }}>
                            <Table
                                config={tableConfig}
                                rowsCount={places.length}
                                testId={tableTestId}
                            />
                        </CopyButtonContext.Provider>
                    )}
                </div>
            )}
        </>

    );
};

export default memo(NewTopChains);